import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, MapPin, Zap, DollarSign, FileText, Clock, CheckCircle, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SubsidyCalculator = () => {
  const [activeSchemes, setActiveSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    charger_type: 'DC_FAST',
    capacity_kw: '',
    location_type: 'URBAN',
    state: 'DELHI',
    project_cost: '',
    has_renewable: false,
    applicant_type: 'GO4GARAGE',
    // Notification settings
    notify_phone: '',
    notification_channel: 'sms'
  });

  useEffect(() => {
    loadActiveSchemes();
  }, []);

  const loadActiveSchemes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/subsidy/schemes/active`);
      const data = await response.json();
      if (data.success) {
        setActiveSchemes(data.schemes);
      }
    } catch (err) {
      console.error('Failed to load schemes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalculate = async () => {
    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        charger_type: formData.charger_type,
        capacity_kw: parseFloat(formData.capacity_kw),
        location_type: formData.location_type,
        state: formData.state,
        project_cost: parseFloat(formData.project_cost),
        has_renewable: formData.has_renewable,
        applicant_type: formData.applicant_type,
        // Include notification settings if phone provided
        ...(formData.notify_phone && {
          notify_phone: formData.notify_phone,
          notification_channel: formData.notification_channel
        })
      };

      const response = await fetch(`${API_URL}/api/subsidy/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Calculation failed');
      }

      setResult(data);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  };

  const getChargerTypeLabel = (type) => {
    const labels = {
      'DC_FAST': 'DC Fast Charger (50-200 kW)',
      'AC_FAST': 'AC Fast Charger (7.4-50 kW)',
      'AC_SLOW': 'AC Slow Charger (3.3-22 kW)'
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Government Subsidy Calculator</h1>
          <p className="text-gray-600 mt-1">Auto-calculate FAME-III + State subsidies for EV charging infrastructure</p>
        </div>
        <div className="flex gap-2">
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            {activeSchemes.length} Active Schemes
          </span>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6">
            {[
              { num: 1, title: 'Charger Details', icon: Zap },
              { num: 2, title: 'Location & Cost', icon: MapPin },
              { num: 3, title: 'Review', icon: FileText },
              { num: 4, title: 'Results', icon: Calculator }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 ${step >= s.num ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                    {s.title}
                  </span>
                </div>
                {idx < 3 && (
                  <div className={`h-1 flex-1 mx-2 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Subsidy Calculation Wizard</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: Charger Details */}
              {step === 1 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Step 1: Charger Specifications</h3>
                  
                  <div>
                    <Label>Charger Type *</Label>
                    <Select value={formData.charger_type} onValueChange={(val) => handleInputChange('charger_type', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DC_FAST">{getChargerTypeLabel('DC_FAST')}</SelectItem>
                        <SelectItem value="AC_FAST">{getChargerTypeLabel('AC_FAST')}</SelectItem>
                        <SelectItem value="AC_SLOW">{getChargerTypeLabel('AC_SLOW')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Charging Capacity (kW) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 120"
                      value={formData.capacity_kw}
                      onChange={(e) => handleInputChange('capacity_kw', e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.charger_type === 'DC_FAST' && 'Range: 50-200 kW'}
                      {formData.charger_type === 'AC_FAST' && 'Range: 7.4-50 kW'}
                      {formData.charger_type === 'AC_SLOW' && 'Range: 3.3-22 kW'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="renewable"
                      checked={formData.has_renewable}
                      onChange={(e) => handleInputChange('has_renewable', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="renewable" className="cursor-pointer">
                      Renewable Energy Integration (Solar/Wind) - Additional ₹1L+ bonus
                    </Label>
                  </div>

                  <Button onClick={() => setStep(2)} className="w-full">
                    Next: Location & Cost
                  </Button>
                </div>
              )}

              {/* Step 2: Location & Cost */}
              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Step 2: Location & Project Cost</h3>
                  
                  <div>
                    <Label>Location Type *</Label>
                    <Select value={formData.location_type} onValueChange={(val) => handleInputChange('location_type', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="URBAN">Urban (Metro/City)</SelectItem>
                        <SelectItem value="RURAL">Rural (20% Bonus)</SelectItem>
                        <SelectItem value="HIGHWAY">Highway Corridor (15% Bonus)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>State *</Label>
                    <Select value={formData.state} onValueChange={(val) => handleInputChange('state', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DELHI">Delhi</SelectItem>
                        <SelectItem value="MAHARASHTRA">Maharashtra</SelectItem>
                        <SelectItem value="KARNATAKA">Karnataka</SelectItem>
                        <SelectItem value="GUJARAT">Gujarat</SelectItem>
                        <SelectItem value="TAMIL_NADU">Tamil Nadu</SelectItem>
                        <SelectItem value="UTTAR_PRADESH">Uttar Pradesh</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Total Project Cost (₹) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 4500000"
                      value={formData.project_cost}
                      onChange={(e) => handleInputChange('project_cost', e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter total installation + equipment cost</p>
                  </div>

                  {/* Notification Settings */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">📱 Notification Settings (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Phone Number</Label>
                        <Input
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={formData.notify_phone}
                          onChange={(e) => handleInputChange('notify_phone', e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">Receive subsidy results via SMS/WhatsApp</p>
                      </div>
                      <div>
                        <Label>Channel</Label>
                        <Select value={formData.notification_channel} onValueChange={(val) => handleInputChange('notification_channel', val)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                      Back
                    </Button>
                    <Button onClick={() => setStep(3)} className="flex-1">
                      Next: Review
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Step 3: Review Your Details</h3>
                  
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Charger Type:</span>
                      <span className="font-semibold">{getChargerTypeLabel(formData.charger_type)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacity:</span>
                      <span className="font-semibold">{formData.capacity_kw} kW</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Location:</span>
                      <span className="font-semibold">{formData.location_type} - {formData.state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Project Cost:</span>
                      <span className="font-semibold">{formatCurrency(parseFloat(formData.project_cost || 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Renewable Energy:</span>
                      <span className="font-semibold">{formData.has_renewable ? 'Yes ✓' : 'No'}</span>
                    </div>
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                      Back
                    </Button>
                    <Button onClick={handleCalculate} disabled={calculating} className="flex-1">
                      {calculating ? 'Calculating...' : 'Calculate Subsidy'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Results */}
              {step === 4 && result && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-green-700">🎉 Subsidy Calculation Complete!</h3>
                  
                  {/* Total Subsidy Card */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border-2 border-green-300">
                    <div className="text-center">
                      <p className="text-sm text-green-700 mb-2">Total Subsidy Amount</p>
                      <p className="text-5xl font-bold text-green-800">
                        {formatCurrency(result.summary.total_subsidy)}
                      </p>
                      <p className="text-lg text-green-700 mt-2">
                        {result.summary.subsidy_percentage.toFixed(1)}% of Project Cost
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-gray-700">FAME-III Subsidy</span>
                      <span className="font-bold text-blue-700">
                        {formatCurrency(result.subsidy_breakdown.fame3.amount)}
                      </span>
                    </div>
                    
                    {result.subsidy_breakdown.state.available && (
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <span className="text-gray-700">{result.subsidy_breakdown.state.scheme}</span>
                        <span className="font-bold text-purple-700">
                          {formatCurrency(result.subsidy_breakdown.state.amount)}
                        </span>
                      </div>
                    )}
                    
                    {result.subsidy_breakdown.bonuses.total > 0 && (
                      <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <span className="text-gray-700">Additional Bonuses</span>
                        <span className="font-bold text-yellow-700">
                          {formatCurrency(result.subsidy_breakdown.bonuses.total)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg">
                      <span className="text-gray-600">Net Project Cost (After Subsidy)</span>
                      <span className="font-bold text-gray-900">
                        {formatCurrency(result.summary.net_project_cost)}
                      </span>
                    </div>
                  </div>

                  <Button onClick={() => setStep(1)} variant="outline" className="w-full">
                    Calculate Another
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Info & Benefits */}
        <div className="space-y-6">
          {/* Active Schemes Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Active Subsidy Schemes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-500">Loading schemes...</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Central (FAME-III)</span>
                    <span className="text-sm font-semibold text-blue-600">3 schemes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">State Policies</span>
                    <span className="text-sm font-semibold text-purple-600">6 states</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Max Subsidy (DC Fast)</span>
                    <span className="text-sm font-semibold text-green-600">₹80L</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Benefits */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Why Use This Calculator?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">Instant Results</p>
                    <p className="text-xs text-gray-600">Get subsidy estimate in 2 minutes vs 30 days manual calculation</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">Maximize Benefits</p>
                    <p className="text-xs text-gray-600">Auto-calculates all eligible subsidies and bonuses</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FileText className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">Document Checklist</p>
                    <p className="text-xs text-gray-600">Know exactly what documents needed for application</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">EMI Options</p>
                    <p className="text-xs text-gray-600">See financing options for net cost</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sample Subsidy */}
          {result && (
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-blue-700 mb-2">Total Savings</p>
                  <p className="text-3xl font-bold text-blue-800">
                    {formatCurrency(result.summary.total_subsidy)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    on {formatCurrency(parseFloat(formData.project_cost))} project
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubsidyCalculator;
